import classNames from 'classnames';
import snakeCase from 'lodash/snakeCase';
import { ReactElement, useEffect } from 'react';
import { Button, DefaultTheme, Typography } from '@guildeducationinc/recess';
import { clickable } from '@src/analytics';
import { MilestoneKey, useMilestone } from '@src/context/MilestoneContext';
import Divider from '@src/components/Shared/Divider/Divider';
import { useConfetti } from '@src/context/ConfettiContext';
import styles from './Milestone.module.scss';
import LifecycleDatePicker from '../LifecycleDatePicker/LifecycleDatePicker';
import CompletionDateConfirmation from '../CompletionDateConfirmation/CompletionDateConfirmation';

export enum MilestoneVariant {
  Primary = 'primary',
  Secondary = 'secondary',
  Canceled = 'canceled',
  NeedsAttention = 'needsAttention',
  Complete = 'complete',
  NoAction = 'noAction',
}

export interface MilestoneAction {
  clickAction?: () => void;
  href?: string;
  openInNewWindow?: boolean;
  text: string;
}

export interface MilestoneContent {
  label?: string;
  title?: string;
  body?: string | ReactElement;
  actions?: {
    primary?: MilestoneAction;
    secondary?: MilestoneAction;
  };
  variant?: MilestoneVariant;
  order?: number;
  className?: string;
}

export interface MilestoneProps extends MilestoneContent {
  milestoneKey?: MilestoneKey;
  setStartingSlide: (slide: number) => void;
}

/**
 * @name Milestone
 * @description A card that displays information about a task for a Member to complete.
 */
const Milestone = ({
  actions,
  body,
  label,
  milestoneKey,
  title,
  variant,
  order,
  className,
  setStartingSlide,
}: MilestoneProps) => {
  const milestone: MilestoneContent | undefined = useMilestone(milestoneKey, order);

  const milestoneLabel = label || milestone?.label;
  const milestoneTitle = title || milestone?.title;
  const milestoneBody = body || milestone?.body;
  const milestonePrimaryAction = actions?.primary || milestone?.actions?.primary;
  const milestoneSecondaryAction = actions?.secondary || milestone?.actions?.secondary;
  const milestoneVariant = variant || milestone?.variant || MilestoneVariant.Primary;
  const milestoneOrder = order || null;

  const milestoneClassNames = classNames(
    'borderRadius--2',
    'flex',
    'flexDirection--column',
    styles.base,
    styles[milestoneVariant],
    className && styles[className],
    milestone?.variant === 'primary' && 'boxShadow--sm'
  );

  const fontColor =
    milestoneVariant === MilestoneVariant.Canceled || milestoneVariant === MilestoneVariant.Complete
      ? DefaultTheme.textColorInverse
      : DefaultTheme.textColor;

  const inverseColorCard =
    milestoneVariant === MilestoneVariant.Canceled ||
    milestoneVariant === MilestoneVariant.Complete;

  const primaryActionAppearance =
    milestoneVariant === MilestoneVariant.Primary ? 'primary' : 'outline';

  const { openConfetti } = useConfetti();

  useEffect(() => {
    let subscribed = true;

    if (subscribed && milestoneKey === MilestoneKey.CompleterActualCompletionDateSaved) {
      openConfetti();
    }

    return () => {
      subscribed = false;
    };
  }, [milestoneKey, openConfetti]);

  return (
    <div className={milestoneClassNames} data-testid={`milestoneCard-${milestoneOrder}`}>
      <Typography color={fontColor} variant='body1' weight='semiBold' className='lowercase'>
        {milestoneLabel}
      </Typography>

      <div className='margin--top_8'>
        <Divider inverseColor={inverseColorCard} />
      </div>

      <Typography
        color={fontColor}
        gutterBottom='md'
        gutterTop='sm'
        variant='h3'
        variantMapping={{ h3: 'h2' }}
        weight='300'
      >
        {milestoneTitle}
      </Typography>
      <Typography
        color={fontColor}
        className='flex--1'
        gutterBottom='lg'
        variant='body1'
        weight='normal'
      >
        {milestoneBody}
      </Typography>

      <div className={styles.milestoneActions}>
        {milestonePrimaryAction && (
          <Button
            data-testid='milestoneButton-primary'
            appearance={primaryActionAppearance}
            className={styles.primaryAction}
            href={milestonePrimaryAction.href}
            target={milestonePrimaryAction.openInNewWindow ? '_blank' : '_self'}
            newWindowIcon={milestonePrimaryAction.openInNewWindow}
            onClick={() => {
              if (milestonePrimaryAction.clickAction) milestonePrimaryAction.clickAction();
              clickable({
                product: 'home',
                tag: `learning_lifecycles_carousel_${milestoneOrder}`,
                text: snakeCase(milestonePrimaryAction?.text),
                type: 'button',
              });
            }}
          >
            {milestonePrimaryAction.text}
          </Button>
        )}
        {milestoneSecondaryAction && (
          <Button
            data-testid='milestoneButton-secondary'
            appearance='ghost'
            className={styles.secondaryAction}
            href={milestoneSecondaryAction.href}
            target={milestoneSecondaryAction.openInNewWindow ? '_blank' : '_self'}
            newWindowIcon={milestoneSecondaryAction.openInNewWindow}
            onClick={() => {
              if (milestoneSecondaryAction.clickAction) milestoneSecondaryAction.clickAction();
              clickable({
                product: 'home',
                tag: `learning_lifecycles_carousel_${milestoneOrder}`,
                text: snakeCase(milestoneSecondaryAction?.text),
                type: 'link',
              });
            }}
          >
            {milestoneSecondaryAction.text}
          </Button>
        )}
      </div>

      {milestoneKey === MilestoneKey.LearnerSubmitExpectedCompletionDate && (
        <LifecycleDatePicker setStartingSlide={setStartingSlide} />
      )}
      {milestoneKey === MilestoneKey.CompleterExpectedCompletionDatePassed && (
        <CompletionDateConfirmation setStartingSlide={setStartingSlide} />
      )}
    </div>
  );
};

export default Milestone;
